import React from 'react';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';

// 0-5 intensity gauge. Same column on filtering_rules; semantics differ by mode:
//
//   listening: 0 off, 1 occasional, 2 sometimes, 3 normal, 4 prioritize, 5 always pin
//   fence:     0 off, 1-2 de-emphasize, 3 filter, 4 hide most, 5 hard block

type Mode = 'listening' | 'fence';

const LISTENING_LABELS: Record<number, string> = {
  0: 'Off',
  1: 'Occasionally surface',
  2: 'Sometimes surface',
  3: 'Normal',
  4: 'Prioritize',
  5: 'Always show first',
};

const FENCE_LABELS: Record<number, string> = {
  0: 'Off',
  1: 'De-emphasize',
  2: 'De-emphasize more',
  3: 'Filter',
  4: 'Hide almost all',
  5: 'Hard block',
};

export function gaugeLabel(mode: Mode, value: number): string {
  const lookup = mode === 'listening' ? LISTENING_LABELS : FENCE_LABELS;
  return lookup[Math.max(0, Math.min(5, value))] ?? `${value}`;
}

interface IntensityGaugeSliderProps {
  mode: Mode;
  value: number;
  onChange: (value: number) => void;
}

const MODE_DESCRIPTION: Record<Mode, string> = {
  listening: 'Slide toward 5 to see much more of this, toward 0 to see less.',
  fence: 'Slide toward 5 to block this almost entirely, toward 0 to just de-emphasize it.',
};

export const IntensityGaugeSlider: React.FC<IntensityGaugeSliderProps> = ({
  mode,
  value,
  onChange,
}) => {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium">Intensity</span>
        <span className="text-xs text-muted-foreground">
          {value} / 5 — {gaugeLabel(mode, value)}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">{MODE_DESCRIPTION[mode]}</p>
      <Slider
        min={0}
        max={5}
        step={1}
        value={[value]}
        onValueChange={(v) => onChange(v[0] ?? 0)}
      />
      <div className="grid grid-cols-6 gap-0.5 text-[10px] text-muted-foreground text-center">
        <span>Off</span>
        <span>1</span>
        <span>2</span>
        <span>3</span>
        <span>4</span>
        <span>5</span>
      </div>
    </div>
  );
};

// Small inline indicator for rule cards — five dots, filled up to the gauge value.
interface IntensityGaugeBadgeProps {
  mode: Mode;
  value: number;
}

export const IntensityGaugeBadge: React.FC<IntensityGaugeBadgeProps> = ({ mode, value }) => {
  const safe = Math.max(0, Math.min(5, value));
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-border/60 bg-muted/40"
      title={`${gaugeLabel(mode, safe)} (${safe}/5)`}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          className={`block h-1.5 w-1.5 rounded-full ${
            n <= safe ? 'bg-foreground' : 'bg-foreground/20'
          }`}
        />
      ))}
      <span className="ml-1 text-[10px] text-muted-foreground tabular-nums">{safe}/5</span>
    </span>
  );
};
